import { BuilderAnalysisClass } from '../types/enums/BuilderAnalysisClass.enum';
import { BuilderAnalysisType as BuilderAnalysisTypeId } from '../types/enums/BuilderAnalysisType.enum';
import { BuilderCatalogClass } from '../types/enums/BuilderCatalogClass.enum';
import { BuilderCharacteristic } from '../types/enums/BuilderCharacteristic.enum';
import { BuilderAnalysisType } from '../types/SimilarlySituated/BuilderAnalysisType.type';
import { BuilderBox, BuilderCondition } from '../types/SimilarlySituated/BuilderBox.type';
import {
  BuilderBoxCreate,
  BuilderConditionBoxCreate,
  BuilderConditionCreate,
  BuilderConditionFormDataCreate,
} from '../types/SimilarlySituated/BuilderBoxCreate.type';
import { BuilderBoxAnalysesPayload, BuilderBoxAnalysis } from '../types/SimilarlySituated/BuilderBoxAnalysis.type';
import { CaseType } from '../types/SimilarlySituated/CaseType.type.ts';
import { SsbCondition } from '../types/SimilarlySituated/SsbCondition.type';
import { SsbOutcome } from '../types/SimilarlySituated/SsbOutcome.type';
import { StatuteKind } from '../types/SimilarlySituated/StatuteKind.type';
import {
  conditionLabel,
  deriveCaseTypeName,
  isConditionComplete,
  isDispositionAvailable,
  sortStages,
} from './ssbCondition';
import { findCharacteristic } from './ssbCatalogMetadata';

const BOX_WIDE_TRAITS_NAME = 'Box-wide traits';

export const MAX_CONDITION_BOXES = 100;
export const MAX_CONDITIONS = 100;

export const conditionBoxCount = (caseTypes: CaseType[], boxTraits: SsbCondition[]): number =>
  caseTypes.length + (boxTraits.length > 0 ? 1 : 0);

export interface DefineState {
  caseTypes: CaseType[];
  boxTraits: SsbCondition[];
}

function toSsbCondition(condition: BuilderCondition): SsbCondition {
  const form = condition.form_data ?? {};

  const mapped: SsbCondition = {
    id: crypto.randomUUID(),
    characteristicId: condition.type.id,
    statutes: form.statutes,
    stages: form.stages,
    statuteLevel: form.statute_level ?? undefined,
    position: form.stage_position ?? undefined,
  };

  switch (condition.type.id) {
    case BuilderCharacteristic.OFFENSE_GROUPING:
      mapped.groupings = form.offence_grouping_types;
      break;
    case BuilderCharacteristic.CASE_DISPOSITION:
      mapped.value = form.case_disposition ?? undefined;
      break;
    case BuilderCharacteristic.AGE:
      mapped.value = form.age;
      break;
    case BuilderCharacteristic.GENDER:
      mapped.value = form.gender;
      break;
    case BuilderCharacteristic.CODEFENDANT:
      mapped.value = form.codefendant_status;
      break;
    case BuilderCharacteristic.DATE_WINDOW:
      mapped.value = form.date_window_stage;
      mapped.dateFrom = form.start_date ?? null;
      mapped.dateTo = form.end_date ?? null;
      break;
    default:
      // statute identity types carry the disposition as a facet instead of a value
      mapped.disposition = form.case_disposition ?? undefined;
  }

  return mapped;
}

/** Maps a stored Box into the Define step. */
export function builderBoxToDefineState(box: BuilderBox): DefineState {
  const caseTypes: CaseType[] = [];
  const boxTraits: SsbCondition[] = [];

  box.condition_boxes.forEach((conditionBox) => {
    if (conditionBox.is_exclude) {
      boxTraits.push(...conditionBox.conditions.map(toSsbCondition));
      return;
    }

    const caseType: CaseType = {
      id: crypto.randomUUID(),
      conditions: conditionBox.conditions.filter((c) => !c.is_except).map(toSsbCondition),
      excepts: conditionBox.conditions.filter((c) => c.is_except).map(toSsbCondition),
    };
    // a stored name matching the derived one was never typed by the user, so it keeps following the conditions
    if (conditionBox.name !== deriveCaseTypeName(caseType)) caseType.customName = conditionBox.name;

    caseTypes.push(caseType);
  });

  return { caseTypes, boxTraits };
}

export function isConditionSavable(condition: SsbCondition): boolean {
  if (!isConditionComplete(condition)) return false;

  const characteristic = findCharacteristic(condition.characteristicId);
  if (characteristic?.conditionClass === BuilderCatalogClass.DATE_RANGE) {
    return Boolean(condition.dateFrom && condition.dateTo);
  }

  return true;
}

function toFormData(condition: SsbCondition): BuilderConditionFormDataCreate {
  switch (condition.characteristicId) {
    case BuilderCharacteristic.OFFENSE_GROUPING:
      return { offence_grouping_type_ids: (condition.groupings ?? []).map((grouping) => grouping.id) };
    case BuilderCharacteristic.CASE_DISPOSITION:
      return { case_disposition_id: condition.value?.id };
    case BuilderCharacteristic.AGE:
      return { age_id: condition.value?.id };
    case BuilderCharacteristic.GENDER:
      return { gender_id: condition.value?.id };
    case BuilderCharacteristic.CODEFENDANT:
      return { codefendant_status_id: condition.value?.id };
    case BuilderCharacteristic.DATE_WINDOW:
      return {
        date_window_stage_id: condition.value?.id,
        start_date: condition.dateFrom ?? undefined,
        end_date: condition.dateTo ?? undefined,
      };
    default:
      // statute identity types: facets the composer leaves unset are sent as null
      return {
        statute_ids: (condition.statutes ?? []).map((statute) => statute.id),
        // sorted so a reorder of the same stages is not seen as a change
        stage_ids: sortStages(condition.stages).map((stage) => stage.id),
        statute_level_id: condition.statuteLevel?.id ?? null,
        stage_position_id: condition.position?.id ?? null,
        case_disposition_id: isDispositionAvailable(condition.stages) ? (condition.disposition?.id ?? null) : null,
      };
  }
}

function conditionKey(condition: SsbCondition): string {
  const formData = Object.entries(toFormData(condition)).map(([key, value]) => [
    key,
    Array.isArray(value) ? [...value].sort((a, b) => a - b) : value,
  ]);

  return JSON.stringify([condition.characteristicId, formData]);
}

export function isSameCondition(a: SsbCondition, b: SsbCondition): boolean {
  return conditionKey(a) === conditionKey(b);
}

function toConditionPayload(condition: SsbCondition, isExcept: boolean): BuilderConditionCreate {
  return {
    name: conditionLabel(condition),
    is_except: isExcept,
    type_id: condition.characteristicId,
    form_data: toFormData(condition),
  };
}

function toConditionBoxes(caseTypes: CaseType[], boxTraits: SsbCondition[]): BuilderConditionBoxCreate[] {
  const conditionBoxes: BuilderConditionBoxCreate[] = [];

  caseTypes.forEach((caseType) => {
    const conditions = caseType.conditions.filter(isConditionSavable);
    // a card that only subtracts is rejected by the API, and an empty one carries nothing
    if (conditions.length === 0) return;

    conditionBoxes.push({
      name: deriveCaseTypeName(caseType),
      is_exclude: false,
      conditions: [
        ...conditions.map((condition) => toConditionPayload(condition, false)),
        ...caseType.excepts.filter(isConditionSavable).map((condition) => toConditionPayload(condition, true)),
      ],
    });
  });

  const traits = boxTraits.filter(isConditionSavable);
  if (traits.length > 0) {
    conditionBoxes.push({
      name: BOX_WIDE_TRAITS_NAME,
      is_exclude: true,
      conditions: traits.map((condition) => toConditionPayload(condition, false)),
    });
  }

  return conditionBoxes;
}

/** The define state is saveable once at least one case type card holds a complete condition. */
export function canSaveDefineState(caseTypes: CaseType[], boxTraits: SsbCondition[]): boolean {
  return toConditionBoxes(caseTypes, boxTraits).some((conditionBox) => !conditionBox.is_exclude);
}

export function defineStateToBuilderBoxPayload(
  name: string,
  caseTypes: CaseType[],
  boxTraits: SsbCondition[],
): BuilderBoxCreate {
  return { name, condition_boxes: toConditionBoxes(caseTypes, boxTraits) };
}

/** Whether two define states would save the same box. */
export function isSameDefineState(a: DefineState, b: DefineState): boolean {
  return (
    JSON.stringify(toConditionBoxes(a.caseTypes, a.boxTraits)) ===
    JSON.stringify(toConditionBoxes(b.caseTypes, b.boxTraits))
  );
}

/** Whether the define state differs from what the build has saved, i.e. a rebuild is needed. */
export function hasDefineChanges(build: BuilderBox, caseTypes: CaseType[], boxTraits: SsbCondition[]): boolean {
  return !isSameDefineState({ caseTypes, boxTraits }, builderBoxToDefineState(build));
}

/** Whether an outcome is picked per statute rather than toggled on its own. */
export const isStatuteAnalysisType = (type: BuilderAnalysisType) =>
  type.builder_analysis_type_class.id === BuilderAnalysisClass.STATUTE_IDENTITY;

const ANALYSIS_STATUTE_KINDS: Partial<Record<BuilderAnalysisTypeId, StatuteKind>> = {
  [BuilderAnalysisTypeId.WOBBLER_FILED_AT_FELONY_LEVEL]: 'wobbler',
  [BuilderAnalysisTypeId.WOBBLER_CONVICTED_AT_FELONY_LEVEL]: 'wobbler',
  [BuilderAnalysisTypeId.SPECIAL_ALLEGATION_ALLEGED]: 'enhancement',
  [BuilderAnalysisTypeId.SPECIAL_ALLEGATION_FOUND_TRUE]: 'enhancement',
};

/** Which statutes a statute outcome is picked from. */
export const analysisStatuteKind = (type: BuilderAnalysisType): StatuteKind =>
  ANALYSIS_STATUTE_KINDS[type.id as BuilderAnalysisTypeId] ?? 'charge';

/** Analyses payload for the selected outcomes, a statute outcome is sent once per statute. */
export function outcomesToAnalysesPayload(outcomes: SsbOutcome[]): BuilderBoxAnalysesPayload {
  return {
    analysis_types: outcomes.flatMap(({ analysisTypeId, statutes }) =>
      statutes.length > 0
        ? statutes.map((statute) => ({ analysis_type_id: analysisTypeId, analysis_type_statute_id: statute.id }))
        : [{ analysis_type_id: analysisTypeId }],
    ),
  };
}

/** Outcome selection saved on the build, one outcome per analysis type holding all of its statutes. */
export function analysesToOutcomes(analyses: BuilderBoxAnalysis[]): SsbOutcome[] {
  const outcomes = new Map<number, SsbOutcome>();

  analyses.forEach(({ builder_analysis_type: type, statute }) => {
    const outcome = outcomes.get(type.id) ?? { analysisTypeId: type.id, statutes: [] };
    if (statute) outcome.statutes.push(statute);
    outcomes.set(type.id, outcome);
  });

  return [...outcomes.values()];
}

const analysesKeys = (outcomes: SsbOutcome[]) =>
  outcomesToAnalysesPayload(outcomes)
    .analysis_types.map((analysis) => `${analysis.analysis_type_id}:${analysis.analysis_type_statute_id ?? ''}`)
    .sort();

/** Whether a saved analysis is still part of the outcome selection. */
export function isAnalysisSelected(analysis: BuilderBoxAnalysis, outcomes: SsbOutcome[]): boolean {
  const outcome = outcomes.find((o) => o.analysisTypeId === analysis.builder_analysis_type.id);
  if (!outcome) return false;

  return analysis.statute ? outcome.statutes.some((statute) => statute.id === analysis.statute?.id) : true;
}

/** Whether the outcome selection is exactly what the build has saved. */
export function matchesSavedAnalyses(analyses: BuilderBoxAnalysis[], outcomes: SsbOutcome[]): boolean {
  return JSON.stringify(analysesKeys(analysesToOutcomes(analyses))) === JSON.stringify(analysesKeys(outcomes));
}
